import React, { useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  useGetAdminMarketplaceItemsQuery,
  useAdminUpdateMarketplaceItemMutation,
  useAdminDeleteMarketplaceItemMutation,
} from "../../../../store/slices/api";
import { SharedTextInput, SharedButton, SharedTag } from "../../../shared";
import { formatPrice } from "../../Marketplace/Items/itemFormat";
import { serverReason } from "../../Marketplace/serverReason";

dayjs.extend(utc);
dayjs.extend(timezone);

const MAX_TITLE_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 1000;

const CATEGORY_COLORS = {
  paintings: { bg: "#E8D5F5", text: "#6B21A8" },
  furniture: { bg: "#D5E8F5", text: "#1E40AF" },
  "knick-knacks": { bg: "#F5E8D5", text: "#92400E" },
  letters: { bg: "#D5F5E8", text: "#065F46" },
  journals: { bg: "#F5D5E8", text: "#9D174D" },
  memorabilia: { bg: "#F5F5D5", text: "#854D0E" },
  "property rental": { bg: "#D5F5F5", text: "#155E75" },
  other: { bg: "#E5E5E5", text: "#374151" },
};

function ItemRow({ item, individualId }) {
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editTitle, setEditTitle] = useState(item.title);
  const [editDescription, setEditDescription] = useState(item.description);
  const [editPrice, setEditPrice] = useState(
    item.price != null ? String(item.price) : ""
  );
  const [editIsSold, setEditIsSold] = useState(item.is_sold);
  const [saveError, setSaveError] = useState(null);
  const [deleteError, setDeleteError] = useState(false);

  const [updateItem, { isLoading: isUpdating }] =
    useAdminUpdateMarketplaceItemMutation();
  const [deleteItem, { isLoading: isDeleting }] =
    useAdminDeleteMarketplaceItemMutation();

  const handleStartEdit = () => {
    setEditTitle(item.title);
    setEditDescription(item.description);
    setEditPrice(item.price != null ? String(item.price) : "");
    setEditIsSold(item.is_sold);
    setSaveError(null);
    setIsEditing(true);
  };

  // Stay in the editor (with the edits kept) if the save fails.
  const handleSaveEdit = async () => {
    setSaveError(null);
    try {
      await updateItem({
        id: item.id,
        individual_id: individualId,
        title: editTitle,
        description: editDescription,
        price: editPrice ? parseFloat(editPrice) : null,
        is_sold: Boolean(editIsSold),
      }).unwrap();
      setIsEditing(false);
    } catch (err) {
      const reason = serverReason(err);
      setSaveError(reason ? `That didn't save: ${reason}` : "That didn't save. Please try again.");
    }
  };

  const handleDelete = async () => {
    setDeleteError(false);
    try {
      await deleteItem({ id: item.id, individual_id: individualId }).unwrap();
    } catch {
      setDeleteError(true);
    }
  };

  const formattedDate = dayjs(item.created_at)
    .utc()
    .local()
    .format("MMM D, YYYY");

  const categoryColor = CATEGORY_COLORS[item.category] || CATEGORY_COLORS.other;

  if (isEditing) {
    return (
      <div className="flex flex-col rounded-[16px] shadow-md p-[16px] border border-dupontGold gap-y-[12px] bg-dupontIvory dark:bg-darkCard">
        <div className="text-[16px] font-bold text-dupontBlue dark:text-darkText">
          Editing Item
        </div>
        <SharedTextInput
          label="Title*"
          name="edit-title"
          value={editTitle}
          onChange={setEditTitle}
          variant="outlined"
          maxChars={MAX_TITLE_LENGTH}
        />
        <SharedTextInput
          label="Description*"
          name="edit-description"
          value={editDescription}
          onChange={setEditDescription}
          multiline
          rows={3}
          variant="outlined"
          maxChars={MAX_DESCRIPTION_LENGTH}
        />
        <SharedTextInput
          label="Price"
          name="edit-price"
          value={editPrice}
          onChange={setEditPrice}
          variant="outlined"
        />
        <label className="flex flex-row items-center gap-x-[8px] cursor-pointer">
          <input
            type="checkbox"
            checked={editIsSold}
            onChange={(e) => setEditIsSold(e.target.checked)}
            className="w-[16px] h-[16px]"
          />
          <span className="text-[14px] text-dupontBlue dark:text-darkText">Mark as Sold</span>
        </label>
        <div className="flex flex-row gap-x-[10px]">
          <SharedButton
            disabled={!(editTitle || "").trim() || !(editDescription || "").trim() || isUpdating}
            onClick={handleSaveEdit}
            loadingText={isUpdating ? "Saving..." : undefined}
          >
            Save Changes
          </SharedButton>
          <SharedButton color="secondary" onClick={() => setIsEditing(false)}>
            Cancel
          </SharedButton>
        </div>
        {saveError && (
          <span className="text-[14px] text-red-700 dark:text-red-400" aria-live="polite">
            {saveError}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col rounded-[16px] shadow-md p-[16px] border border-dupontGold/30 dark:border-darkGold/20 bg-white dark:bg-darkCard gap-y-[8px]">
      <div className="flex flex-row justify-between items-start">
        <div className="flex flex-col gap-y-[4px]">
          <div className="flex flex-row items-center gap-x-[8px]">
            <div className="text-[17px] font-bold text-dupontBlue dark:text-darkText">
              {item.title}
            </div>
            {item.is_sold && (
              <span className="text-[11px] font-bold text-white bg-red-400 px-[8px] py-[2px] rounded-full">
                SOLD
              </span>
            )}
          </div>
          <div className="text-[13px] text-dupontGray dark:text-darkMuted">
            by {item.seller_name} &middot; {formattedDate}
          </div>
        </div>
        <div className="flex flex-row items-center gap-x-[8px]">
          <SharedTag
            text={item.category}
            color={categoryColor.bg}
            textColor={categoryColor.text}
          />
          {formatPrice(item.price) && (
            <span className="text-[15px] font-bold text-dupontBlue dark:text-darkText">
              {formatPrice(item.price)}
            </span>
          )}
        </div>
      </div>
      <div className="text-[15px] line-clamp-2 whitespace-pre-wrap text-dupontBlue dark:text-darkText">
        {item.description}
      </div>
      <div className="flex flex-row gap-x-[10px] pt-[8px] border-t border-dupontGold/30 dark:border-darkGold/20 items-center">
        {showDeleteConfirm ? (
          <div className="flex flex-row items-center gap-x-[10px]">
            <span className="text-[14px] text-red-600">
              Delete this item?
            </span>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="text-[13px] font-bold text-red-600 hover:text-red-800 disabled:opacity-50"
            >
              {isDeleting ? "Deleting..." : "Yes, Delete"}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="text-[13px] font-bold text-dupontGray hover:text-dupontBlue"
            >
              Cancel
            </button>
          </div>
        ) : (
          <>
            <button
              onClick={handleStartEdit}
              className="text-[13px] font-bold text-dupontGoldDk hover:text-dupontGold"
            >
              Edit
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="text-[13px] font-bold text-red-400 hover:text-red-600"
            >
              Delete
            </button>
          </>
        )}
        {deleteError && (
          <span className="text-[14px] text-red-700 dark:text-red-400" aria-live="polite">
            {"That item couldn't be deleted. Please try again."}
          </span>
        )}
      </div>
    </div>
  );
}

function MarketplaceManagement({ individualId }) {
  const { data: items, isFetching } =
    useGetAdminMarketplaceItemsQuery(individualId);

  if (isFetching || !items) {
    return null;
  }

  if (items.length === 0) {
    return (
      <div className="text-[15px] text-dupontGray dark:text-darkMuted">
        No marketplace items yet.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-[16px]">
      <div className="text-[14px] text-dupontGray dark:text-darkMuted">
        {items.length} item{items.length !== 1 ? "s" : ""} total
      </div>
      {items.map((item) => (
        <ItemRow key={item.id} item={item} individualId={individualId} />
      ))}
    </div>
  );
}

export default MarketplaceManagement;
